import { supabase } from '../lib/supabase';
import { StockData } from '../types';
import { trackApiCall } from './performance';
import { parseLocalDate, toLocalDateString, parseTimestamp, toTimestampString } from './dateHelpers';

export interface DatabaseMetadata {
  id: string;
  name: string;
  recordCount: number;
  lastModified: Date;
  createdAt: Date;
  description?: string;
}

export interface DatabaseInfo extends DatabaseMetadata {
  data: StockData[];
}

// Funzioni helper per conversioni sicure
const toNumericOrNull = (value: any): number | null => {
  if (value === null || value === undefined || value === '' || value === 'N/A' || value === 'n/a' || value === '#N/A') {
    return null;
  }
  const num = Number(value);
  return isFinite(num) && !isNaN(num) ? num : null;
};

const toNumericOrZero = (value: any): number => {
  if (value === null || value === undefined || value === '' || value === 'N/A' || value === 'n/a' || value === '#N/A') {
    return 0;
  }
  const num = Number(value);
  return isFinite(num) && !isNaN(num) ? num : 0;
};

const toBigIntOrNull = (value: any): number | null => {
  const numValue = toNumericOrNull(value);
  return numValue !== null ? Math.round(numValue) : null;
};

const toBigIntOrZero = (value: any): number => {
  const numValue = toNumericOrZero(value);
  return Math.round(numValue);
};


// Cache per i database
const databaseCache = new Map<string, {
  data: StockData[];
  timestamp: number;
  hash: string; // Add a hash to verify data integrity
}>();

// Helper function to generate a simple hash of data
const generateDataHash = (data: any[]): string => {
  try {
    // Use the first 5 and last 5 items to create a hash
    // This is a simple approach - for production, consider a more robust hashing algorithm
    const sampleItems = [...data.slice(0, 5), ...data.slice(-5)];
    const jsonString = JSON.stringify(sampleItems);
    let hash = 0;
    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(16);
  } catch (e) {
    console.error('Error generating data hash:', e);
    return Date.now().toString(16); // Fallback
  }
};

// Funzione helper per convertire i dati Supabase in StockData
const convertSupabaseToStockData = (supabaseData: any[]): StockData[] => {
  return supabaseData.map(row => ({
    Date: parseLocalDate(row.date) || new Date(),
    Ticker: row.ticker,
    Open: Number(row.open),
    High: Number(row.high),
    Low: Number(row.low),
    Close: Number(row.close),
    Volume: Number(row.volume),
    'Market Cap': Number(row.market_cap) || 0,
    'Shares Outstanding': Number(row.shares_outstanding) || 0,
    '1 Min High': Number(row.one_min_high) || 0,
    '5 Min High': Number(row.five_min_high) || 0,
    '5 Min Low': Number(row.five_min_low) || 0,
    'Low Before 12': Number(row.low_before_12) || 0,
    'Low After 12': Number(row.low_after_12) || 0,
    'High 10-11': Number(row.high_10_11) || 0,
    'Low 10-11': Number(row.low_10_11) || 0,
    'Volume 10-11': Number(row.volume_10_11) || 0,
    'High 11-12': Number(row.high_11_12) || 0,
    'Low 11-12': Number(row.low_11_12) || 0,
    'Volume 11-12': Number(row.volume_11_12) || 0,
    'High 12-13': Number(row.high_12_13) || 0,
    'Low 12-13': Number(row.low_12_13) || 0,
    'Volume 12-13': Number(row.volume_12_13) || 0,
    'High 13-14': Number(row.high_13_14) || 0,
    'Low 13-14': Number(row.low_13_14) || 0,
    'Volume 13-14': Number(row.volume_13_14) || 0,
    'High 14-15': Number(row.high_14_15) || 0,
    'Low 14-15': Number(row.low_14_15) || 0,
    'Volume 14-15': Number(row.volume_14_15) || 0,
    'High 15-16': Number(row.high_15_16) || 0,
    'Low 15-16': Number(row.low_15_16) || 0,
    'Volume 15-16': Number(row.volume_15_16) || 0,
    'High 30m': Number(row.high_30m) || 0,
    'Low 30m': Number(row.low_30m) || 0,
    'Volume 30m': Number(row.volume_30m) || 0,
    'Hod Time': row.hod_time ? parseTimestamp(row.hod_time) : null,
    'Lod Time': row.lod_time ? parseTimestamp(row.lod_time) : null,
    'Pre Market High': Number(row.pre_market_high) || 0,
    'Pre Market High Time': row.pre_market_high_time ? parseLocalDate(row.pre_market_high_time) : null,
    'Pre Market Low': Number(row.pre_market_low) || 0,
    'Pre Market Low Time': row.pre_market_low_time ? parseLocalDate(row.pre_market_low_time) : null,
    'Pre Market Volume': Number(row.pre_market_volume) || 0,
    'Gap %': Number(row.gap_percent) || 0,
  }));
};

// Funzione helper per convertire StockData in formato Supabase con gestione sicura delle date
const convertStockDataToSupabase = (data: StockData[], databaseId: string) => {
  return data.map(row => ({
    database_id: databaseId,
    date: toLocalDateString(row.Date), // Solo la data - già validata in FileUpload
    ticker: row.Ticker,
    // Campi NOT NULL - usa toNumericOrZero per garantire un valore numerico
    open: toNumericOrZero(row.Open),
    high: toNumericOrZero(row.High),
    low: toNumericOrZero(row.Low),
    close: toNumericOrZero(row.Close),
    volume: toBigIntOrZero(row.Volume),
    // Campi nullable - usa toNumericOrNull per permettere NULL
    market_cap: toBigIntOrNull(row['Market Cap']),
    shares_outstanding: toBigIntOrNull(row['Shares Outstanding']),
    one_min_high: toNumericOrNull(row['1 Min High']),
    five_min_high: toNumericOrNull(row['5 Min High']),
    five_min_low: toNumericOrNull(row['5 Min Low']),
    low_before_12: toNumericOrNull(row['Low Before 12']),
    low_after_12: toNumericOrNull(row['Low After 12']),
    high_10_11: toNumericOrNull(row['High 10-11']),
    low_10_11: toNumericOrNull(row['Low 10-11']),
    volume_10_11: toBigIntOrNull(row['Volume 10-11']),
    high_11_12: toNumericOrNull(row['High 11-12']),
    low_11_12: toNumericOrNull(row['Low 11-12']),
    volume_11_12: toBigIntOrNull(row['Volume 11-12']),
    high_12_13: toNumericOrNull(row['High 12-13']),
    low_12_13: toNumericOrNull(row['Low 12-13']),
    volume_12_13: toBigIntOrNull(row['Volume 12-13']),
    high_13_14: toNumericOrNull(row['High 13-14']),
    low_13_14: toNumericOrNull(row['Low 13-14']),
    volume_13_14: toBigIntOrNull(row['Volume 13-14']),
    high_14_15: toNumericOrNull(row['High 14-15']),
    low_14_15: toNumericOrNull(row['Low 14-15']),
    volume_14_15: toBigIntOrNull(row['Volume 14-15']),
    high_15_16: toNumericOrNull(row['High 15-16']),
    low_15_16: toNumericOrNull(row['Low 15-16']),
    volume_15_16: toBigIntOrNull(row['Volume 15-16']),
    high_30m: toNumericOrNull(row['High 30m']),
    low_30m: toNumericOrNull(row['Low 30m']),
    volume_30m: toBigIntOrNull(row['Volume 30m']),
    // Date fields - usa la funzione helper per gestire date sicure
    hod_time: toTimestampString(row['Hod Time']),
    lod_time: toTimestampString(row['Lod Time']),
    pre_market_high: toNumericOrNull(row['Pre Market High']),
    pre_market_high_time: toLocalDateString(row['Pre Market High Time']),
    pre_market_low: toNumericOrNull(row['Pre Market Low']),
    pre_market_low_time: toLocalDateString(row['Pre Market Low Time']),
    pre_market_volume: toBigIntOrNull(row['Pre Market Volume']),
    gap_percent: toNumericOrNull(row['Gap %']),
  }));
};

// Ottiene la lista di tutti i database salvati - OTTIMIZZATO
export const getDatabaseList = async (): Promise<DatabaseMetadata[]> => {
  return trackApiCall('getDatabaseList', async () => {
    try {
      const { data, error } = await supabase
        .from('databases')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Errore nel caricamento della lista database:', error);
        throw new Error(`Errore nel caricamento dei database: ${error.message}`);
      }

      // Converte direttamente i dati senza verifiche aggiuntive
      // Il record_count è mantenuto accurato dai trigger del database
      const databases = (data || []).map((db) => ({
        id: db.id,
        name: db.name,
        recordCount: db.record_count || 0, // Usa direttamente il valore dal database
        lastModified: new Date(db.updated_at),
        createdAt: new Date(db.created_at),
        description: db.description || undefined,
      }));

      console.log(`Caricati ${databases.length} database dalla lista`);
      return databases;
    } catch (error) {
      console.error('Errore nel caricamento della lista database:', error);
      throw error;
    }
  });
};

// Verify database cache integrity
export const verifyDatabaseCache = async (name: string): Promise<boolean> => {
  return trackApiCall(`verifyDatabaseCache:${name}`, async () => {
    try {
      const cachedData = databaseCache.get(name);
      if (!cachedData) return false;
      
      // Get the database metadata to check record count
      const { data: dbData, error: dbError } = await supabase
        .from('databases')
        .select('record_count, updated_at')
        .eq('name', name)
        .maybeSingle();
        
      if (dbError || !dbData) return false;
      
      // Check if the record count matches
      if (cachedData.data.length !== dbData.record_count) {
        console.warn(`[Cache Integrity] Record count mismatch for "${name}": cache=${cachedData.data.length}, db=${dbData.record_count}`);
        return false;
      }
      
      // Check if the database has been updated since we cached it
      const dbLastModified = new Date(dbData.updated_at).getTime();
      if (cachedData.timestamp < dbLastModified) {
        console.warn(`[Cache Integrity] Cache is outdated for "${name}"`);
        return false;
      }
      
      // Verify the data hash
      const currentHash = generateDataHash(cachedData.data);
      if (currentHash !== cachedData.hash) {
        console.warn(`[Cache Integrity] Data hash mismatch for "${name}"`);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error(`Error verifying cache for "${name}":`, error);
      return false;
    }
  });
};

// Carica un database specifico per nome con caching
export const loadDatabase = async (name: string): Promise<StockData[] | null> => {
  return trackApiCall(`loadDatabase:${name}`, async () => {
    const startTime = performance.now();
    
    try {
      // Check cache first
      const cachedData = databaseCache.get(name);
      if (cachedData && Date.now() - cachedData.timestamp < 5 * 60 * 1000) { // 5 minutes cache
        console.log(`[Cache] Using cached data for database "${name}" (age: ${((Date.now() - cachedData.timestamp) / 1000).toFixed(1)}s)`);
        return cachedData.data;
      }

      // Prima ottieni l'ID del database
      const { data: dbData, error: dbError } = await supabase
        .from('databases')
        .select('id, record_count, updated_at')
        .eq('name', name)
        .maybeSingle();

      if (dbError) {
        throw new Error(`Errore nel caricamento del database: ${dbError.message}`);
      }

      if (!dbData) {
        console.warn(`Database "${name}" non trovato`);
        return null;
      }

      // Check if we have a cached version that matches the updated_at timestamp
      if (cachedData) {
        const dbLastModified = new Date(dbData.updated_at).getTime();
        if (cachedData.timestamp >= dbLastModified) {
          console.log(`[Cache] Using cached data for database "${name}" (still valid after server check)`);
          return cachedData.data;
        }
      }

      console.log(`Caricamento database "${name}" con ${dbData.record_count} record attesi...`);

      // Carica tutti i dati stock per questo database
      // Usa un approccio paginato per gestire database molto grandi
      let allData: any[] = [];
      let from = 0;
      const pageSize = 1000; // Ridotto a 1000 per rispettare il limite di Supabase
      let hasMore = true; 
      let pageCount = 0;
      let totalTime = 0;

      const loadStartTime = performance.now();
      
      while (hasMore) {
        const pageStartTime = performance.now();
        pageCount++;
        
        // Add timeout to each request
        const timeoutPromise = new Promise<{ error: { message: string } }>((resolve) => {
          setTimeout(() => resolve({ error: { message: 'Request timeout after 8 seconds' } }), 8000);
        });
        
        const fetchPromise = supabase
          .from('stock_data')
          .select('*')
          .eq('database_id', dbData.id)
          .order('date', { ascending: false })
          .range(from, from + pageSize - 1);
        
        // Race between the fetch and the timeout
        const { data: stockData, error: stockError } = await Promise.race([
          fetchPromise,
          timeoutPromise
        ]);
        
        const pageEndTime = performance.now();
        const pageTime = pageEndTime - pageStartTime;
        totalTime += pageTime;
        
        console.log(`[Performance] Page ${pageCount} loaded in ${pageTime.toFixed(2)}ms`);
        
        if (stockError) {
          if (stockError.message.includes('timeout')) {
            console.error(`Timeout loading page ${pageCount} of database "${name}"`);
            throw new Error(`Database loading timeout. This may indicate network issues or that your Supabase project is not properly configured. Please check your .env file and Supabase project status.`);
          } else {
            throw new Error(`Errore nel caricamento dei dati stock: ${stockError.message}`);
          }
        }

        if (!stockData || stockData.length === 0) {
          hasMore = false;
        } else {
          allData = allData.concat(stockData);
          from += pageSize;
          hasMore = stockData.length === pageSize;
          
          console.log(`Caricati ${allData.length}/${dbData.record_count} record... (${(allData.length / dbData.record_count * 100).toFixed(1)}%)`);
        }
      }

      const loadEndTime = performance.now();
      const totalLoadTime = loadEndTime - loadStartTime;
      console.log(`[Performance] Loaded ${allData.length} records in ${totalLoadTime.toFixed(2)}ms (${pageCount} pages, avg ${(totalTime / pageCount).toFixed(2)}ms per page)`);

      // Verify data integrity
      if (allData.length !== dbData.record_count) {
        console.warn(`[Data Integrity] Expected ${dbData.record_count} records but loaded ${allData.length}`);
      }

      const convertedData = convertSupabaseToStockData(allData);
      
      // Generate a hash for data integrity verification
      const dataHash = generateDataHash(convertedData);
      
      // Update cache
      databaseCache.set(name, {
        data: convertedData,
        timestamp: Date.now(),
        hash: dataHash
      });
      
      console.log(`Database "${name}" caricato con successo: ${convertedData.length} record recuperati`);

      return convertedData;
    } catch (error) {
      console.error(`Errore nel caricamento del database "${name}":`, error);
      
      // If we have a cached version, use it as fallback with a warning
      const cachedData = databaseCache.get(name);
      if (cachedData) {
        console.warn(`[Cache] Using cached data as fallback after load error for "${name}"`);
        return cachedData.data;
      }
      
      throw error;
    } finally {
      const endTime = performance.now();
      console.log(`[Performance] Total loadDatabase operation took ${(endTime - startTime).toFixed(2)}ms`);
    }
  });
};

// Load database with retry logic
export const loadDatabaseWithRetry = async (name: string, maxRetries = 3): Promise<StockData[] | null> => {
  return trackApiCall(`loadDatabaseWithRetry:${name}`, async () => {
    let retries = 0;
    
    while (retries < maxRetries) {
      try {
        // First check if we have a valid cache
        if (await verifyDatabaseCache(name)) {
          const cachedData = databaseCache.get(name);
          console.log(`[Cache] Using verified cache for "${name}"`);
          return cachedData?.data || null;
        }
        
        // If no valid cache, try to load from database
        return await loadDatabase(name);
      } catch (error) {
        retries++;
        console.error(`Error loading database "${name}" (attempt ${retries}/${maxRetries}):`, error);
        
        if (retries >= maxRetries) {
          // If we have a cached version, use it as last resort
          const cachedData = databaseCache.get(name);
          if (cachedData) {
            console.warn(`[Cache] Using cached data as last resort after ${maxRetries} failed attempts for "${name}"`);
            return cachedData.data;
          }
          
          throw error;
        }
        
        // Wait before retrying with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retries), 8000);
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    return null; // Should never reach here due to throw in the last iteration
  });
};

// Salva un nuovo database o sovrascrive uno esistente
export const saveDatabase = async (name: string, data: StockData[], description?: string): Promise<boolean> => {
  return trackApiCall(`saveDatabase:${name}`, async () => {
    try {
      // Valida il nome del database
      if (!name || name.trim().length === 0) {
        throw new Error('Il nome del database non può essere vuoto');
      }

      if (data.length === 0) {
        throw new Error('Non è possibile salvare un database vuoto');
      }

      const sanitizedName = name.trim();

      // Controlla se esiste già un database con questo nome
      const { data: existingDb, error: checkError } = await supabase
        .from('databases')
        .select('id')
        .eq('name', sanitizedName)
        .maybeSingle();

      if (checkError) {
        throw new Error(`Errore nella verifica del database esistente: ${checkError.message}`);
      }

      let databaseId: string;

      if (existingDb) {
        // Database esistente - elimina tutti i dati esistenti
        databaseId = existingDb.id;
        
        console.log(`Sovrascrittura database esistente "${sanitizedName}"...`);
        
        const { error: deleteError } = await supabase
          .from('stock_data')
          .delete()
          .eq('database_id', databaseId);

        if (deleteError) {
          throw new Error(`Errore nell'eliminazione dei dati esistenti: ${deleteError.message}`);
        }

        // Aggiorna i metadati del database
        const { error: updateError } = await supabase
          .from('databases')
          .update({
            description: description || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', databaseId);

        if (updateError) {
          throw new Error(`Errore nell'aggiornamento del database: ${updateError.message}`);
        }
      } else {
        // Nuovo database - crea
        console.log(`Creazione nuovo database "${sanitizedName}"...`);
        
        const { data: newDb, error: createError } = await supabase
          .from('databases')
          .insert({
            name: sanitizedName,
            description: description || null,
            record_count: 0, // Inizializza a 0, sarà aggiornato dal trigger
          })
          .select('id')
          .single();

        if (createError) {
          throw new Error(`Errore nella creazione del database: ${createError.message}`);
        }

        databaseId = newDb.id;
      }

      // Inserisci i dati in batch per migliorare le performance
      const batchSize = 1000; // Ridotto a 1000 per rispettare il limite di Supabase
      const stockDataForSupabase = convertStockDataToSupabase(data, databaseId);
      let totalInserted = 0;

      console.log(`Inserimento di ${data.length} record in batch di ${batchSize}...`);
      
      const startTime = performance.now();

      for (let i = 0; i < stockDataForSupabase.length; i += batchSize) {
        const batch = stockDataForSupabase.slice(i, i + batchSize);
        
        const { error: insertError } = await supabase
          .from('stock_data')
          .insert(batch);

        if (insertError) {
          throw new Error(`Errore nell'inserimento dei dati (batch ${Math.floor(i / batchSize) + 1}): ${insertError.message}`);
        }

        totalInserted += batch.length;
        console.log(`Inseriti ${totalInserted}/${data.length} record...`);
      }
      
      const endTime = performance.now();
      console.log(`[Performance] Inserted ${totalInserted} records in ${(endTime - startTime).toFixed(2)}ms`);

      // Clear cache for this database
      clearDatabaseCache(sanitizedName);

      // Il record_count sarà aggiornato automaticamente dal trigger
      console.log(`Database "${sanitizedName}" salvato con successo: ${totalInserted} record inseriti`);

      return true;
    } catch (error) {
      console.error('Errore nel salvataggio del database:', error);
      throw error;
    }
  });
};

// Unisce nuovi dati a un database esistente
export const mergeDataIntoDatabase = async (name: string, newData: StockData[]): Promise<boolean> => {
  return trackApiCall(`mergeDataIntoDatabase:${name}`, async () => {
    try {
      // Prima ottieni l'ID del database
      const { data: dbData, error: dbError } = await supabase
        .from('databases')
        .select('id')
        .eq('name', name)
        .maybeSingle();

      if (dbError) {
        throw new Error(`Errore nella ricerca del database: ${dbError.message}`);
      }

      if (!dbData) {
        throw new Error(`Database "${name}" non trovato per l'unione`);
      }

      if (newData.length === 0) {
        console.warn('Nessun nuovo dato da unire');
        return true;
      }

      console.log(`Unione di ${newData.length} nuovi record al database "${name}"...`);

      // Ottieni i dati esistenti per identificare duplicati (usa paginazione per database grandi)
      let existingData: any[] = [];
      let from = 0;
      const pageSize = 1000; // Ridotto a 1000 per rispettare il limite di Supabase
      let hasMore = true;

      while (hasMore) {
        const { data: pageData, error: existingError } = await supabase
          .from('stock_data')
          .select('date, ticker')
          .eq('database_id', dbData.id)
          .range(from, from + pageSize - 1);

        if (existingError) {
          throw new Error(`Errore nel caricamento dei dati esistenti: ${existingError.message}`);
        }

        if (!pageData || pageData.length === 0) {
          hasMore = false;
        } else {
          existingData = existingData.concat(pageData);
          from += pageSize;
          hasMore = pageData.length === pageSize;
        }
      }

      // Crea una mappa per identificare duplicati basata su Date + Ticker
      const existingMap = new Set<string>();
      existingData.forEach(row => {
        const key = `${row.date}_${row.ticker}`;
        existingMap.add(key);
      });

      // Filtra i nuovi dati per evitare duplicati
      const uniqueNewData = newData.filter(row => {
        const key = `${toLocalDateString(row.Date)}_${row.Ticker}`;
        return !existingMap.has(key);
      });

      if (uniqueNewData.length === 0) {
        console.warn('Tutti i nuovi dati sono duplicati, nessun dato aggiunto');
        return true;
      }

      console.log(`${uniqueNewData.length} record unici da inserire (${newData.length - uniqueNewData.length} duplicati ignorati)`);

      // Inserisci i nuovi dati in batch
      const batchSize = 1000; // Ridotto a 1000 per rispettare il limite di Supabase
      const stockDataForSupabase = convertStockDataToSupabase(uniqueNewData, dbData.id);
      
      const startTime = performance.now();

      for (let i = 0; i < stockDataForSupabase.length; i += batchSize) {
        const batch = stockDataForSupabase.slice(i, i + batchSize);
        
        const { error: insertError } = await supabase
          .from('stock_data')
          .insert(batch);

        if (insertError) {
          throw new Error(`Errore nell'inserimento dei nuovi dati (batch ${Math.floor(i / batchSize) + 1}): ${insertError.message}`);
        }
      }
      
      const endTime = performance.now();
      console.log(`[Performance] Merged ${uniqueNewData.length} records in ${(endTime - startTime).toFixed(2)}ms`);

      // Aggiorna il timestamp del database
      const { error: updateError } = await supabase
        .from('databases')
        .update({ 
          updated_at: new Date().toISOString()
        })
        .eq('id', dbData.id);

      if (updateError) {
        console.warn('Errore nell\'aggiornamento del database:', updateError);
      }

      // Clear cache for this database
      clearDatabaseCache(name);

      console.log(`Unione completata: ${uniqueNewData.length} nuovi record aggiunti`);

      return true;
    } catch (error) {
      console.error(`Errore nell'unione dei dati nel database "${name}":`, error);
      throw error;
    }
  });
};

// Elimina un database
export const deleteDatabase = async (name: string): Promise<boolean> => {
  return trackApiCall(`deleteDatabase:${name}`, async () => {
    try {
      // Prima ottieni l'ID del database
      const { data: dbData, error: dbError } = await supabase
        .from('databases')
        .select('id')
        .eq('name', name)
        .maybeSingle();

      if (dbError) {
        throw new Error(`Errore nella ricerca del database: ${dbError.message}`);
      }

      if (!dbData) {
        console.warn(`Database "${name}" non trovato per l'eliminazione`);
        return true; // Considera come successo se non esiste
      }

      // Elimina il database (i dati stock verranno eliminati automaticamente per CASCADE)
      const { error: deleteError } = await supabase
        .from('databases')
        .delete()
        .eq('id', dbData.id);

      if (deleteError) {
        throw new Error(`Errore nell'eliminazione del database: ${deleteError.message}`);
      }

      // Clear cache for this database
      clearDatabaseCache(name);

      console.log(`Database "${name}" eliminato con successo`);
      return true;
    } catch (error) {
      console.error(`Errore nell'eliminazione del database "${name}":`, error);
      throw error;
    }
  });
};

// Clear cache for a specific database or all databases
export const clearDatabaseCache = (name?: string) => {
  if (name) {
    databaseCache.delete(name);
    console.log(`[Cache] Cleared cache for database "${name}" at ${new Date().toISOString()}`);
  } else {
    databaseCache.clear();
    console.log(`[Cache] Cleared all database caches at ${new Date().toISOString()}`);
  }
};

// Calcola le statistiche di utilizzo dei database
export const getDatabaseStorageInfo = async () => {
  return trackApiCall('getDatabaseStorageInfo', async () => {
    try {
      const databases = await getDatabaseList();
      
      const totalRecords = databases.reduce((sum, db) => sum + db.recordCount, 0);
      
      return {
        totalDatabases: databases.length,
        totalRecords,
        totalSizeBytes: 0, // Non applicabile per Supabase
        totalSizeMB: '∞', // Supabase gestisce lo storage
        totalLocalStorageUsed: '0.00', // Non usiamo più localStorage
        storageUsagePercentage: '0.0', // Non applicabile
        isNearLimit: false // Non ci sono limiti con Supabase
      };
    } catch (error) {
      console.error('Errore nel calcolo delle informazioni di storage:', error);
      return {
        totalDatabases: 0,
        totalRecords: 0,
        totalSizeBytes: 0,
        totalSizeMB: '0.00',
        totalLocalStorageUsed: '0.00',
        storageUsagePercentage: '0.0',
        isNearLimit: false
      };
    }
  });
};